import { ArrowLeft, CheckCircle2, Lightbulb, AlertCircle } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Link, useLocation } from "react-router";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { useActividadAprendiz } from "@/hooks/actividadAprendiz/useActividadAprendiz";
import { useEffect } from "react";
import { useNavigate } from "react-router";

export default function InstruccionesActividad() {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuarioInfo } = useCurrentUser();
  const { cerebro, actividad, programaId, actividadesPrograma } =
    location.state || {};

  // Obtener datos de la actividad del aprendiz
  const { data: actividadAprendiz, isLoading, isError } = useActividadAprendiz(
    actividad?.id,
    usuarioInfo?.aprendiz?.id,
  );

  useEffect(() => {
    if (!isLoading && actividadAprendiz) {
      if (
        actividadAprendiz.intentos > 0 ||
        (actividadAprendiz.ultimoIntento && actividadAprendiz.ultimoIntento.id)
      ) {
        // Si ya tiene intentos, redirigir directo a la actividad
        navigate(`/actividad/${actividad?.id}`, {
          state: { cerebro, actividad, programaId, actividadesPrograma },
          replace: true,
        });
      }
    }
  }, [
    isLoading,
    actividadAprendiz,
    actividad,
    cerebro,
    programaId,
    actividadesPrograma,
    navigate,
  ]);

  // Mostrar mensaje de error si la actividad no está asignada o ocurrió un error
  if (!isLoading && (isError || actividadAprendiz === null)) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b flex flex-col items-center justify-center p-4"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, var(--linear-gradient-start), var(--linear-gradient-end))",
          backgroundColor: "var(--background-muted)",
        }}
      >
        <div className="w-full max-w-2xl">
          <div className="bg-white dark:bg-[var(--card-background)] rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200 dark:border-gray-700">
            {/* Botón de regresar */}
            <Link
              to="/programas"
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Volver a programas</span>
            </Link>

            {/* Icono de alerta */}
            <div className="flex flex-col items-center text-center py-8">
              <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
                <AlertCircle
                  className="text-yellow-600 dark:text-yellow-400"
                  size={48}
                />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                Actividad no disponible
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Esta actividad no te ha sido asignada aún. Contacta a tu
                administrador para más información.
              </p>
              <Link to="/programas">
                <Button className="bg-primary px-6 py-4">
                  Volver a mis programas
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const instrucciones = [
    <>
      Este reto debe reflejar una <strong>situación real</strong> de tu trabajo
      cotidiano.
    </>,
    <>
      No busques respuestas perfectas, busca <strong>claridad</strong> y{" "}
      <strong>aplicabilidad inmediata</strong>.
    </>,
    <>
      La retroalimentación está diseñada para ayudarte a mejorar,{" "}
      <strong>no para evaluarte</strong> o calificarte.
    </>,
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white dark:bg-[var(--card-background)] rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200 dark:border-gray-700">
          {/* Botón de regresar */}
          <Link
            to="/programas"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Volver a programas</span>
          </Link>

          {/* Título con icono */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary dark:bg-primary rounded-lg">
              <Lightbulb
                className="text-white dark:text-[var(--foreground)]"
                size={28}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--foreground)] dark:text-[var(--foreground)]">
              Antes de comenzar
            </h1>
          </div>

          {/* Indicaciones */}
          <div className="space-y-4 mb-6">
            {instrucciones.map((instruccion, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2
                  className="text-green-500 dark:text-green-300 flex-shrink-0 mt-0.5"
                  size={24}
                />
                <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed">
                  {instruccion}
                </p>
              </div>
            ))}
          </div>

          {/* Línea divisora */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>

          {/* Botón continuar */}
          <div className="flex justify-end">
            <Link
              to={`/actividad/${actividad?.id}`}
              state={{
                cerebro,
                actividad,
                programaId,
                actividadesPrograma,
              }}
            >
              <Button className="bg-primary px-6 py-4">
                Entendido, ir a la actividad
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
